/**
 * List-page view state that survives opening a lead and coming back.
 *
 * Written after reps lost their All Clients filters every time they opened a
 * client: the list kept its filters in component state, and App unmounts the
 * list to show the lead file — so coming back built a fresh one.
 *
 *   - THE URL HOLDS THE VIEW. Every filter, the search text, sort and page live
 *     in the /clients query string (useUrlParam), read on mount. Changes REPLACE
 *     the current history entry — typing a search must not leave thirty Back
 *     steps. A default value is left out, so the default view has no params.
 *   - SCROLL IS REMEMBERED PER LIST URL (useListScroll) and put back only when
 *     the list is returned to — the lead file's back link or browser Back/Forward
 *     (App calls armListScroll). A fresh visit from the sidebar starts at the top.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

const LIST_PATH = '/clients';
const here = () => window.location.pathname + window.location.search;
/** The mounted list's URL as it last wrote it — the key its scroll is saved under. */
let listUrl = '';
/** Record the list URL after a change made outside writeParams (App's Follow-Ups view push). */
export function noteListUrl() { if (window.location.pathname === LIST_PATH) listUrl = here(); }

export function readParam(name: string): string {
  return new URLSearchParams(window.location.search).get(name) ?? '';
}

/** Set (or, for '' / null, remove) query params on the current list URL, in place. */
export function writeParams(patch: Record<string, string | null>) {
  if (window.location.pathname !== LIST_PATH) return;
  const q = new URLSearchParams(window.location.search);
  for (const [k, v] of Object.entries(patch)) {
    if (v == null || v === '') q.delete(k); else q.set(k, v);
  }
  const s = q.toString();
  const url = LIST_PATH + (s ? `?${s}` : '');
  if (url !== here()) window.history.replaceState(window.history.state, '', url);
  listUrl = url;
}

/** useState backed by ?name=. `def` is the value meant by no param, and is never written. */
export function useUrlParam(name: string, def = ''): [string, (v: string) => void] {
  const [value, setValue] = useState(() => readParam(name) || def);
  const set = useCallback((v: string) => {
    setValue(v);
    writeParams({ [name]: v === def ? null : v });
  }, [name, def]);
  return [value, set];
}

/** A useEffect that skips the mount: "the view changed", never "the view loaded". */
export function useOnChange(fn: () => void, deps: React.DependencyList) {
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    fn();
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}

// ---- scroll -----------------------------------------------------------------
const savedScroll = new Map<string, number>();
let armed = false;

/** The next list to mount is a RETURN (back link, Back/Forward): put its scroll back. */
export function armListScroll() { armed = true; }
/** A fresh visit (sidebar, tab, badge): start at the top. */
export function disarmListScroll() { armed = false; }

/**
 * Remember `ref`'s scroll under the list URL, and restore it on a return.
 *
 * The rows arrive after mount (a fetch, and a loading screen first), so the
 * restore retries each frame until the list is tall enough, for up to 4s — and
 * gives up the moment the rep scrolls or types themselves.
 */
export function useListScroll(ref: React.RefObject<HTMLElement>) {
  const top = useRef(0);

  useEffect(() => {
    noteListUrl();
    const onScroll = (e: Event) => { if (e.target === ref.current) top.current = (e.target as HTMLElement).scrollTop; };
    document.addEventListener('scroll', onScroll, true);

    const target = armed ? savedScroll.get(here()) : undefined;
    armed = false;
    let frame = 0;
    let stopped = false;
    const stop = () => { stopped = true; };
    if (target) {
      const until = Date.now() + 4000;
      const tick = () => {
        if (stopped) return;
        const el = ref.current;
        if (el && (el.scrollHeight - el.clientHeight >= target || Date.now() > until)) {
          el.scrollTop = target;
          return;
        }
        if (Date.now() > until) return;
        frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
      window.addEventListener('wheel', stop, { passive: true });
      window.addEventListener('touchstart', stop, { passive: true });
      window.addEventListener('keydown', stop);
    }

    return () => {
      document.removeEventListener('scroll', onScroll, true);
      cancelAnimationFrame(frame);
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchstart', stop);
      window.removeEventListener('keydown', stop);
      savedScroll.set(listUrl, top.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
